import { useState } from 'react'
import { Link } from '@inertiajs/react'
import * as PopoverPrimitive from '@radix-ui/react-popover'

import { Popover, PopoverContent } from '@components/atoms/Popover'
import cn from '@components/utils/cn'
import { whenRange } from '@utils/callbackTime'

import {
  INITIAL_CALLBACKS,
  isMineOrOpen,
  statusFor,
  type Callback,
  type CallbackStatus,
} from '../pages/CallbackRequestsPreview'

/**
 * The sidebar's reminders pop-up for the callbacks demo: your callbacks and
 * unassigned ones that are due, then the ones coming up. Styled after the
 * roster board's ChangeBreakdown ledger. Sample data from the Calls preview.
 */

const COMING_UP_DAYS = 3

type OpenStatus = Exclude<CallbackStatus, 'done'>

const TONE: Record<OpenStatus, { label: string; color: string }> = {
  overdue: { label: 'Overdue', color: 'tw:text-error-500' },
  due_now: { label: 'Due now', color: 'tw:text-warning-500' },
  today: { label: 'Today', color: 'tw:text-primary-500' },
  upcoming: { label: 'Coming up', color: 'tw:text-gray-400' },
}

const isDue = (status: CallbackStatus) => status === 'overdue' || status === 'due_now'

export function callbackNotifications(now: Date) {
  const horizon = now.getTime() + COMING_UP_DAYS * 86_400_000
  const open = INITIAL_CALLBACKS.filter((cb) => isMineOrOpen(cb) && !cb.completedAt)
    .filter((cb) => new Date(cb.dueAt).getTime() <= horizon)
    .sort((a, b) => a.dueAt.localeCompare(b.dueAt))
    .map((cb) => ({ callback: cb, status: statusFor(cb, now) as OpenStatus }))

  return {
    due: open.filter(({ status }) => isDue(status)),
    comingUp: open.filter(({ status }) => !isDue(status)),
  }
}

function Marker({ status, showLine }: { status: OpenStatus; showLine: boolean }) {
  return (
    <span
      className={cn(
        'tw:flex tw:w-1.5 tw:shrink-0 tw:flex-col tw:items-center tw:self-stretch',
        TONE[status].color
      )}
    >
      <span className="tw:mt-1 tw:h-1.5 tw:w-1.5 tw:shrink-0 tw:rounded-full tw:bg-current" aria-hidden />
      {showLine && <span className="tw:mt-0.5 tw:w-0.5 tw:flex-1 tw:rounded tw:bg-current tw:opacity-40" aria-hidden />}
    </span>
  )
}

function NotificationRow({
  callback: cb,
  status,
  now,
  showLine,
  href,
  onNavigate,
}: {
  callback: Callback
  status: OpenStatus
  now: Date
  showLine: boolean
  href: string
  onNavigate: () => void
}) {
  return (
    <li className="tw:flex tw:gap-2.5 tw:text-xs">
      <Marker status={status} showLine={showLine} />
      <Link
        href={`${href}&callback=${cb.id}`}
        onClick={onNavigate}
        className="tw:mb-1 tw:flex tw:min-w-0 tw:flex-1 tw:flex-col tw:rounded tw:px-1.5 tw:py-1 tw:text-gray-900 tw:hover:bg-gray-50 tw:focus:outline-hidden tw:focus-visible:ring-2 tw:focus-visible:ring-primary-300"
      >
        <span className="tw:flex tw:items-baseline tw:justify-between tw:gap-2">
          <span className="tw:truncate tw:font-semibold">{cb.supporter.fullName}</span>
          {isDue(status) && (
            <span className={cn('tw:shrink-0 tw:font-semibold', TONE[status].color)}>{TONE[status].label}</span>
          )}
        </span>
        <span className="tw:text-gray-500">
          {whenRange(cb.dueAt, now, cb.flexible)}
          {!cb.assignee && ' · Any leader'}
        </span>
        {cb.notes && <span className="tw:truncate tw:text-gray-500">{cb.notes}</span>}
      </Link>
    </li>
  )
}

export function CallbackNotifications({
  anchor,
  callsHref,
  open,
  onOpenChange,
}: {
  anchor: HTMLElement | null
  // The demo's Calls page; rows add their callback to highlight it there.
  callsHref: string
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const [now] = useState(() => new Date())
  const { due, comingUp } = callbackNotifications(now)
  const close = () => onOpenChange(false)
  const callbacksHref = `${callsHref}?tab=callbacks`

  const list = (rows: typeof due) =>
    rows.length > 0 && (
      <ul className="tw:flex tw:flex-col">
        {rows.map(({ callback, status }, index) => (
          <NotificationRow
            key={callback.id}
            callback={callback}
            status={status}
            now={now}
            showLine={index < rows.length - 1}
            href={callbacksHref}
            onNavigate={close}
          />
        ))}
      </ul>
    )

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverPrimitive.Anchor virtualRef={{ current: anchor }} />
      <PopoverContent
        portal
        side="right"
        align="end"
        sideOffset={12}
        collisionPadding={16}
        aria-label="Reminders"
        className="tw:flex tw:w-80 tw:flex-col tw:gap-3"
        onInteractOutside={(event) => {
          if (anchor?.contains(event.target as Node)) event.preventDefault()
        }}
      >
        <p className="tw:border-b tw:border-gray-100 tw:pb-2 tw:text-xs tw:font-bold tw:text-gray-900">
          {due.length} {due.length === 1 ? 'callback' : 'callbacks'} due
        </p>
        {list(due)}
        {comingUp.length > 0 && (
          <div>
            <p className="tw:mb-2 tw:border-t tw:border-gray-100 tw:pt-2 tw:text-xs tw:font-bold tw:text-gray-900">
              Coming up
            </p>
            {list(comingUp)}
          </div>
        )}
        <Link
          href={callbacksHref}
          onClick={close}
          className="tw:border-t tw:border-gray-100 tw:pt-2 tw:text-xs tw:font-semibold tw:text-primary-700 tw:hover:underline"
        >
          View all callbacks
        </Link>
      </PopoverContent>
    </Popover>
  )
}
